import { api } from './api';

/**
 * D-71: balances, payments on orders, payment links and orders built in
 * Admin (docs/payment-requests.md). Kept beside api.ts so that file stays
 * the catalog of the earlier phases.
 */

export interface OrderBalanceView {
  order_id: string;
  amount_due_cents: number;
  payments_cents: number;
  refunds_cents: number;
  chargebacks_cents: number;
  in_flight_cents: number;
  paid_cents: number;
  balance_due_cents: number;
}

export interface OrderPaymentLine {
  id: string;
  payment_plan_id: string;
  tender_type: string;
  status: string;
  processor: string | null;
  payment_method_family: string | null;
  payment_method: string | null;
  underlying_card_brand: string | null;
  card_brand: string | null;
  card_last4: string | null;
  amount_cents: number;
  captured_amount_cents: number;
  refunded_amount_cents: number;
  check_number: string | null;
  house_account_organization_id: string | null;
  payment_request_id: string | null;
  stripe_payment_intent_id: string | null;
  staff_name: string | null;
  created_at: string;
  captured_at: string | null;
  plan_purpose: string;
  stripe_fee_cents: number | null;
  stripe_net_cents: number | null;
  fee_source: string | null;
  stripe_payout_id: string | null;
  payout_arrival_date: string | null;
  payout_reconciliation_status: string | null;
}

export type AmountPolicy = 'FULL_REMAINING_BALANCE_ONLY' | 'AT_LEAST_MINIMUM' | 'CUSTOM_AMOUNT_ALLOWED' | 'FIXED_REQUEST_AMOUNT';
export type IssuancePolicy = 'ISSUE_IMMEDIATELY' | 'ISSUE_WHEN_PAID' | 'ISSUE_BUT_DISABLE_ADMISSION_UNTIL_PAID';
export type PastDuePolicy = 'NO_AUTOMATIC_ACTION' | 'NOTIFY_STAFF' | 'AUTO_CANCEL_AFTER_GRACE_PERIOD';

export interface PaymentRequestView {
  id: string;
  order_id: string;
  recipient_email_snapshot: string;
  recipient_name_snapshot: string | null;
  status: string;
  amount_policy: AmountPolicy;
  requested_amount_cents: number;
  minimum_amount_cents: number | null;
  due_at: string | null;
  expires_at: string | null;
  created_at: string;
  sent_at: string | null;
  send_count: number;
  last_viewed_at: string | null;
  view_count: number;
  paid_at: string | null;
  revoked_at: string | null;
  events: { event_type: string; actor_type: string; source: string | null; amount_cents: number | null; occurred_at: string; staff_name: string | null }[];
}

export interface OrderPaymentSummary {
  balance: OrderBalanceView;
  payment_status: string;
  payment_due_at: string | null;
  past_due: boolean;
  days_past_due: number;
  ticket_issuance_policy: IssuancePolicy;
  past_due_policy: PastDuePolicy;
  paid_in_full_at: string | null;
  payments: OrderPaymentLine[];
  requests: PaymentRequestView[];
}

export type AdminTenderInput =
  | { tender_type: 'CHECK'; amount_cents: number; check_number?: string | null }
  | { tender_type: 'HOUSE_ACCOUNT'; amount_cents: number; organization_id: string }
  | { tender_type: 'CARD_KEYED'; amount_cents: number; payment_intent_id: string };

export interface BalanceTermsInput {
  issuance_policy: IssuancePolicy;
  due_at: string | null;
  past_due_policy: PastDuePolicy;
}

export interface PaymentRequestInput {
  amount_policy: AmountPolicy;
  requested_amount_cents?: number | null;
  minimum_amount_cents?: number | null;
  due_at?: string | null;
  expires_at?: string | null;
  recipient_email?: string | null;
  recipient_name?: string | null;
  send: boolean;
}

export interface BalanceOrderRow {
  order_id: string;
  order_number: string;
  customer: string | null;
  email: string | null;
  event_title: string | null;
  /** Set on a package order, which is listed by its package rather than its first show (D-330). */
  package_name: string | null;
  performance_starts_at: string | null;
  sales_channel: string;
  payment_status: string;
  total_cents: number;
  paid_cents: number;
  balance_cents: number;
  payment_due_at: string | null;
  days_past_due: number;
  aging: string;
  link_status: string | null;
  link_sent_at: string | null;
}

export interface BalancesResponse {
  summary: { outstanding_orders: number; outstanding_cents: number; past_due_cents: number; due_this_week_cents: number };
  aging: { bucket: string; orders: number; balance_cents: number }[];
  orders: BalanceOrderRow[];
}

export interface WindowCartView {
  performances: { performance_id: string; title: string; starts_at: string; venue_name: string; timezone: string; seats: { unit_id: string; section: string | null; table: string | null; row: string | null; seat_number: string | null; ticket_type_name: string | null; label: string; ga_area_id: string | null }[] }[];
  totals: { face_value_cents: number; discount_cents: number; comp_cents: number; fees_cents: number; tax_cents: number; customer_total_cents: number; tickets: number };
  expires_at: string | null;
}

export interface NewOrderPerformance {
  id: string;
  event_id: string;
  manifest_id: string | null;
  event_title: string;
  starts_at: string;
  timezone: string;
  venue_name: string;
}

export interface WindowPerformanceDetail {
  performance: { id: string; event_title: string };
  zones: { id: string; name: string }[];
  ticket_types: { id: string; name: string; is_comp_type: number }[];
  prices: Record<string, Record<string, { face_cents: number; total_cents: number; fees_cents: number } | null>>;
}

type CardStart = { payment_intent_id: string; client_secret: string | null; amount_cents: number; publishable_key: string | null };

export const paymentEndpoints = {
  summary: (orderId: string) => api.get<OrderPaymentSummary>(`/api/admin/orders/${orderId}/payment`),
  terms: (orderId: string, input: { payment_due_at?: string | null; past_due_policy?: PastDuePolicy }) => api.patch<OrderPaymentSummary>(`/api/admin/orders/${orderId}/payment-terms`, input),
  startCard: (orderId: string, amountCents: number) => api.post<CardStart>(`/api/admin/orders/${orderId}/payments/card`, { amount_cents: amountCents }),
  collect: (orderId: string, input: { tenders: AdminTenderInput[]; receipt_email: boolean; note?: string | null }) =>
    api.post<{ payment: { amount_cents: number; balance_after_cents: number; paid_in_full: boolean }; summary: OrderPaymentSummary }>(`/api/admin/orders/${orderId}/payments`, input),
  createRequest: (orderId: string, input: PaymentRequestInput) => api.post<{ payment_request: PaymentRequestView; url: string }>(`/api/admin/orders/${orderId}/payment-requests`, input),
  resendRequest: (requestId: string, rotate: boolean) => api.post<{ payment_request: PaymentRequestView; url: string | null }>(`/api/admin/orders/payment-requests/${requestId}/resend`, { rotate }),
  copyRequest: (requestId: string) => api.post<{ url: string }>(`/api/admin/orders/payment-requests/${requestId}/copy`),
  revokeRequest: (requestId: string, reason: string | null) => api.post<{ payment_request: PaymentRequestView }>(`/api/admin/orders/payment-requests/${requestId}/revoke`, { reason }),
  cancelBalance: (orderId: string, input: { reason_code: string; reason_text?: string | null }) => api.post<{ summary: OrderPaymentSummary }>(`/api/admin/orders/${orderId}/cancel-balance`, input),
  balances: (params: { event_id?: string; past_due?: boolean }) => {
    const query = new URLSearchParams();
    if (params.event_id) query.set('event_id', params.event_id);
    if (params.past_due) query.set('past_due', '1');
    return api.get<BalancesResponse>(`/api/admin/balances${query.size ? `?${query}` : ''}`);
  },
  // Building an order for a customer: the staff cart (the box office cart endpoints), then POST /new.
  newPerformances: () => api.get<{ performances: NewOrderPerformance[] }>('/api/admin/orders/new/performances'),
  performance: (performanceId: string) => api.get<WindowPerformanceDetail>(`/api/box-office/performances/${performanceId}`),
  cart: () => api.get<WindowCartView>('/api/box-office/cart'),
  hold: (input: { performance_id: string; ticket_type_id: string; mode: 'best'; quantity: number; price_zone_ids?: string[]; allow_split?: boolean }) => api.post<{ held: string[]; cart: WindowCartView }>('/api/box-office/cart/holds', input),
  /** Exact seats from the map (at most 50 per call, which the builder chunks). */
  holdSeats: (input: { performance_id: string; ticket_type_id: string; unit_ids: string[] }) => api.post<{ held: string[]; cart: WindowCartView }>('/api/box-office/cart/holds', { ...input, mode: 'seats' }),
  /** Give seats back out of the staff cart; every seat of the performance when none are named. */
  releaseSeats: (input: { performance_id: string; unit_ids?: string[] }) => api.del<{ released: unknown; cart: WindowCartView }>('/api/box-office/cart/holds', input),
  extendCart: () => api.post<{ expires_at: string | null }>('/api/box-office/cart/extend'),
  clearCart: () => api.del<unknown>('/api/box-office/cart'),
  startNewCard: (amountCents: number) => api.post<CardStart>('/api/admin/orders/new/card', { amount_cents: amountCents }),
  createOrder: (input: { contact: { name: string; email: string; phone?: string | null }; delivery_method: 'MOBILE' | 'EMAIL_PDF' | 'WILL_CALL'; tenders: AdminTenderInput[]; balance: BalanceTermsInput | null; payment_request: PaymentRequestInput | null; email_confirmation: boolean; note?: string | null }) =>
    api.post<{ order_id: string; order_number: string; total_cents: number; tendered_cents: number; balance_due_cents: number; payment_request: { url: string } | null }>('/api/admin/orders/new', input),
};
